import type { ReactNode } from 'react';
import { useBreakpoint } from '@kognitos/lattice';

/**
 * `DialogContent` max-width for a trigger dialog, keyed by whether the assistant
 * is hosted alongside the form. The wide two-pane width is clamped to the
 * viewport so it never overflows narrow screens; without the assistant it falls
 * back to the stock dialog width. Shared by the webhook and poll trigger dialogs
 * so the two can't drift.
 */
export const TRIGGER_DIALOG_MAX_WIDTH_CLASS = {
  withAssistant: 'sm:max-w-[min(1180px,calc(100vw-2rem))]',
  default: 'sm:max-w-2xl',
} as const;

/**
 * Whether the viewport is wide enough to host the assistant beside the form.
 * The two-pane layout reserves a fixed 600px for the panel, so below `lg`
 * (≈992px) the form would be squeezed or clipped. On narrower screens the
 * trigger dialogs fall back to the plain single-column form — the assistant is
 * an enhancement, not required to configure a trigger.
 *
 * @returns `true` when the current breakpoint can fit the two-pane layout
 */
export function useHasRoomForAssistant(): boolean {
  const breakpoint = useBreakpoint();
  return breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === 'xxl';
}

interface ITriggerAssistantDialogLayoutProps {
  /** The dialog body: header, stepper, and step content. */
  children: ReactNode;
  /**
   * The pre-configured `<IntegrationAssistantPanel>`, docked on the left, or
   * `null`/`undefined` when the assistant isn't shown.
   */
  panel?: ReactNode;
}

/**
 * Hosts a trigger dialog's body, optionally next to the integration assistant
 * panel. Extracted from the webhook and poll trigger dialogs, which otherwise
 * duplicate this wrapper verbatim.
 *
 * The body stays at a stable tree position whether or not the panel is shown, so
 * toggling the assistant never remounts the form (which would drop ConfigureStep's
 * transient state). Without a panel the wrapper uses `display: contents`, adding
 * no box so the body lays out exactly like the stock dialog's direct children;
 * with a panel it becomes the two-pane layout — a left-docked panel and the form
 * offset by the panel's fixed width. `gap-5` mirrors `DialogContent`'s own gap so
 * the body spacing is identical in both states.
 *
 * @param props - The dialog body and (optional) assistant panel element
 * @returns The dialog layout
 */
export function TriggerAssistantDialogLayout({
  children,
  panel,
}: ITriggerAssistantDialogLayoutProps) {
  const hasPanel = Boolean(panel);
  return (
    <div
      className={
        hasPanel
          ? 'flex flex-1 min-h-0 -mx-5 overflow-hidden relative'
          : 'contents'
      }
    >
      <div
        // Offset the form by the panel width + a 12px gutter; both sides read
        // the shared --trigger-assistant-panel-width var so they stay in sync.
        className={
          hasPanel
            ? 'flex flex-col gap-5 flex-1 min-w-0 min-h-0 overflow-hidden px-5 ml-[calc(var(--trigger-assistant-panel-width)_+_12px)]'
            : 'contents'
        }
      >
        {children}
      </div>
      {panel}
    </div>
  );
}
